import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Quote cart — globální state "košík" nabídky.
 * Persist v localStorage, aby se zachoval mezi reloady.
 *
 * Klíčová operace je `addItem` — sčítá quantity, pokud už díl se stejným
 * kontextem (article + model + subgroup) v košíku je.
 */

export interface CartItem {
  articleNumber: string;
  articleName: string | null;
  unitPriceNetCzk: string; // decimal jako string
  vatRate: number;
  quantity: number;
  // Kontext pro PDF (rozkres)
  modelId?: string | null;
  modelLabel?: string | null; // např. "1290 Super Adventure R 2018"
  subgroupId?: string | null;
  subgroupLabel?: string | null; // např. "IGNITION SYSTEM"
  subgroupImageUrl?: string | null;
  lineNumber?: number | null;
  articleImageUrl?: string | null;
}

interface QuoteCartCtx {
  items: CartItem[];
  itemCount: number; // sum of quantities
  addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
  removeItem: (index: number) => void;
  updateQty: (index: number, qty: number) => void;
  clear: () => void;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

const STORAGE_KEY = "ktm.quote-cart";

function loadFromStorage(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as CartItem[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveToStorage(items: CartItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // quota exceeded etc. — ignoruj
  }
}

const Ctx = createContext<QuoteCartCtx | null>(null);

export function QuoteCartProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<CartItem[]>(() => loadFromStorage());
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    saveToStorage(items);
  }, [items]);

  const addItem = useCallback<QuoteCartCtx["addItem"]>((newItem) => {
    setItems((prev) => {
      // Najdi existující item se stejným kontextem (article + model + subgroup)
      const idx = prev.findIndex(
        (it) =>
          it.articleNumber === newItem.articleNumber &&
          (it.modelId ?? null) === (newItem.modelId ?? null) &&
          (it.subgroupId ?? null) === (newItem.subgroupId ?? null),
      );
      const qty = newItem.quantity ?? 1;
      if (idx >= 0) {
        const copy = [...prev];
        const existing = copy[idx]!;
        copy[idx] = { ...existing, quantity: existing.quantity + qty };
        return copy;
      }
      const toAdd: CartItem = {
        articleNumber: newItem.articleNumber,
        articleName: newItem.articleName,
        unitPriceNetCzk: newItem.unitPriceNetCzk,
        vatRate: newItem.vatRate,
        quantity: qty,
        modelId: newItem.modelId ?? null,
        modelLabel: newItem.modelLabel ?? null,
        subgroupId: newItem.subgroupId ?? null,
        subgroupLabel: newItem.subgroupLabel ?? null,
        subgroupImageUrl: newItem.subgroupImageUrl ?? null,
        lineNumber: newItem.lineNumber ?? null,
        articleImageUrl: newItem.articleImageUrl ?? null,
      };
      return [...prev, toAdd];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateQty = useCallback((index: number, qty: number) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === index ? { ...it, quantity: Math.max(1, Math.floor(qty)) } : it,
      ),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<QuoteCartCtx>(() => {
    const itemCount = items.reduce((s, it) => s + it.quantity, 0);
    return {
      items,
      itemCount,
      addItem,
      removeItem,
      updateQty,
      clear,
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
    };
  }, [items, drawerOpen, addItem, removeItem, updateQty, clear]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useQuoteCart(): QuoteCartCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useQuoteCart must be used inside <QuoteCartProvider>");
  return ctx;
}
